import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FlaskConical, Hammer, Code, FileText, BarChart3, Lock, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Student-facing labs catalog for codefoundry.sh. Lists the 4 labs the
 * platform supports (4 Engineering Program, 5 Debug+Test, 7 Data Viz, 10
 * Capstone) and routes each card to the right existing screen in Student
 * Mode. Admins also get a "Manage student whitelist" button here.
 */
const LABS = [
  {
    id: 4,
    title: 'Build an Engineering Program',
    desc: 'Describe a small program; the agent returns code, 5 tests and a README. Run it in-browser via Pyodide.',
    duration: '15 min',
    href: '/playground?lab=4',
    icon: Hammer,
    accent: '#818cf8',
  },
  {
    id: 5,
    title: 'Debug & Test',
    desc: 'Paste buggy code; the agent diagnoses the syntax + logic bug, returns a fixed version and 3 unit tests.',
    duration: '15 min',
    href: '/playground?lab=5',
    icon: Code,
    accent: '#34d399',
  },
  {
    id: 7,
    title: 'AI Data Visualisation',
    desc: 'Share a CSV; the agent returns matplotlib code that renders 3 charts in your browser.',
    duration: '15 min',
    href: '/playground?lab=7',
    icon: BarChart3,
    accent: '#fbbf24',
  },
  {
    id: 10,
    title: 'Capstone — Mini Project',
    desc: 'Pick an idea; the agent returns plan + prototype + chart + 5-slide outline + Responsible-AI checklist.',
    duration: '25 min',
    href: '/playground?lab=10',
    icon: FileText,
    accent: '#f472b6',
  },
];

export default function LabsHomePage() {
  const [info, setInfo] = useState({ loading: true });

  useEffect(() => {
    fetch(`${API_URL}/api/labs/whoami`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => setInfo({ loading: false, ...(d || {}) }))
      .catch(() => setInfo({ loading: false }));
  }, []);

  if (info.loading) {
    return <div style={{ padding: '2rem', color: 'hsl(var(--muted-foreground))' }}>Loading…</div>;
  }

  const hasAccess = info.is_labs_access || info.is_student || info.is_admin;

  return (
    <div style={{ padding: '2rem 2rem 4rem', maxWidth: 1100, margin: '0 auto' }} data-testid="labs-home-page">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
          <div style={{ padding: '0.6rem', borderRadius: '0.75rem', background: 'hsla(238, 83%, 67%, 0.1)' }}>
            <FlaskConical size={22} style={{ color: '#818cf8' }} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 600 }}>Student Labs</h1>
            <p style={{ marginTop: '0.4rem', color: 'hsl(var(--muted-foreground))', fontSize: '0.9375rem' }}>
              Short, guided AI exercises. Each lab takes 20–30 minutes.
            </p>
          </div>
        </div>
        {info.is_admin && (
          <Link to="/labs/admin" data-testid="labs-admin-btn"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0.5rem 0.9rem',
              borderRadius: 8, border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))',
              textDecoration: 'none', color: 'hsl(var(--foreground))', fontSize: '0.8125rem', fontWeight: 500,
            }}>
            <Users size={14} /> Manage student whitelist
          </Link>
        )}
      </div>

      {!hasAccess && (
        <div style={{
          marginBottom: '1.5rem', padding: '0.9rem 1rem', borderRadius: 10,
          background: 'hsla(43, 90%, 55%, 0.08)', border: '1px solid hsla(43, 90%, 55%, 0.3)',
          display: 'flex', gap: '0.75rem', alignItems: 'flex-start',
        }} data-testid="labs-locked-banner">
          <Lock size={16} style={{ color: '#d29922', marginTop: 2 }} />
          <div style={{ fontSize: '0.875rem' }}>
            <strong>You can browse every lab here, but running one needs instructor approval.</strong>
            <div style={{ color: 'hsl(var(--muted-foreground))', marginTop: 4, fontSize: '0.8125rem' }}>
              Each lab verifies your email against the labs whitelist on launch. Ask your instructor to add your email.
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
        {LABS.map((lab) => {
          const Icon = lab.icon;
          return (
            <Link
              key={lab.id}
              to={lab.href}
              data-testid={`lab-card-${lab.id}`}
              style={{
                display: 'flex', flexDirection: 'column', gap: '0.75rem',
                padding: '1.1rem 1.1rem 1rem', borderRadius: 12,
                border: '1px solid hsl(var(--border))', background: 'hsl(var(--card))',
                color: 'hsl(var(--foreground))', textDecoration: 'none',
                transition: 'border-color 150ms ease, transform 150ms ease',
                opacity: hasAccess ? 1 : 0.75,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ padding: '0.5rem', borderRadius: 10, background: `${lab.accent}1a` }}>
                  <Icon size={18} style={{ color: lab.accent }} />
                </div>
                <span style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'hsl(var(--muted-foreground))' }}>
                  LAB {lab.id} · {lab.duration}
                </span>
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, lineHeight: 1.3 }}>{lab.title}</h3>
                <p style={{ margin: '0.4rem 0 0', fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', lineHeight: 1.45 }}>
                  {lab.desc}
                </p>
              </div>
              <div style={{ marginTop: 'auto', fontSize: '0.8125rem', fontWeight: 500, color: hasAccess ? '#818cf8' : 'hsl(var(--muted-foreground))' }}>
                {hasAccess ? 'Start →' : 'Preview →'}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
